export function initAccordionBlock(): void {
  const accordionBlocks = document.querySelectorAll<HTMLElement>('.accordion-block');
  if (!accordionBlocks) return;

  accordionBlocks.forEach(block => {
    const items = block.querySelectorAll<HTMLElement>('.accordion-block__item');

    items.forEach((item, index) => {
      const trigger = item.querySelector<HTMLButtonElement>('.accordion-block__item-trigger');
      const panel = item.querySelector<HTMLElement>('.accordion-block__item-content');

      if (!trigger || !panel) return;

      // Initialise collapsed state
      panel.style.height = '0px';
      panel.style.overflow = 'hidden';

      // Remove tabindex from all focusable elements inside the content initially
      makeContentNonTabbable(panel);

      // Handle keyboard events on the trigger (button)
      trigger.addEventListener('keydown', e => {
        switch (e.key) {
          case 'Enter':
          case ' ': // Space
            e.preventDefault();
            toggleAccordion(item, trigger, panel);
            break;
          case 'ArrowDown':
            e.preventDefault();
            focusNextItem(items, index);
            break;
          case 'ArrowUp':
            e.preventDefault();
            focusPreviousItem(items, index);
            break;
          case 'Home':
            e.preventDefault();
            items[0]?.querySelector<HTMLButtonElement>('.accordion-block__item-trigger')?.focus();
            break;
          case 'End':
            e.preventDefault();
            items[items.length - 1]?.querySelector<HTMLButtonElement>('.accordion-block__item-trigger')?.focus();
            break;
        }
      });

      // Handle click events on the trigger
      trigger.addEventListener('click', e => {
        e.preventDefault();
        toggleAccordion(item, trigger, panel);
      });
    });
  });
}

function makeContentNonTabbable(panel: HTMLElement): void {
  // Find all potentially focusable elements
  const focusableElements = panel.querySelectorAll<HTMLElement>(
    'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
  );

  // Remove them from tab order
  focusableElements.forEach(element => {
    element.setAttribute('tabindex', '-1');
  });
}

function toggleAccordion(item: HTMLElement, trigger: HTMLButtonElement, panel: HTMLElement): void {
  const isExpanded = item.classList.contains('accordion-block__item--open');

  item.classList.toggle('accordion-block__item--open', !isExpanded);
  trigger.setAttribute('aria-expanded', (!isExpanded).toString());

  if (isExpanded) {
    collapsePanel(panel);
    // When closing, make sure content is not tabbable
    makeContentNonTabbable(panel);
  } else {
    expandPanel(panel);
    // When opening, restore tabindex for content links
    restoreContentTabbing(panel);
  }
}

function restoreContentTabbing(panel: HTMLElement): void {
  // Restore tab order for links and buttons when accordion is open
  const focusableElements = panel.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled])'
  );

  focusableElements.forEach(element => {
    element.removeAttribute('tabindex');
  });
}

function focusNextItem(items: NodeListOf<HTMLElement>, currentIndex: number): void {
  const nextIndex = (currentIndex + 1) % items.length;
  items[nextIndex]?.querySelector<HTMLButtonElement>('.accordion-block__item-trigger')?.focus();
}

function focusPreviousItem(items: NodeListOf<HTMLElement>, currentIndex: number): void {
  const prevIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
  items[prevIndex]?.querySelector<HTMLButtonElement>('.accordion-block__item-trigger')?.focus();
}

function collapsePanel(panel: HTMLElement): void {
  const panelHeight = panel.scrollHeight;
  panel.style.height = `${panelHeight}px`;

  requestAnimationFrame(() => {
    panel.style.height = '0px';
    panel.style.overflow = 'hidden';
  });
}

function expandPanel(panel: HTMLElement): void {
  const panelHeight = panel.scrollHeight;
  panel.style.height = `${panelHeight}px`;

  const onTransitionEnd = () => {
    panel.style.height = 'auto';
    panel.style.overflow = 'visible';
    panel.removeEventListener('transitionend', onTransitionEnd);
  };

  panel.addEventListener('transitionend', onTransitionEnd);
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAccordionBlock);
} else {
  initAccordionBlock();
}
